import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    TextField,
    Typography,
    Chip,
    Button,
    Alert
} from "@mui/material";
import React, { useState, useRef } from "react";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from "axios";
import { jwtDecode } from "jwt-decode";

const UploadModal = ({
    open,
    onClose,
    onUploadSuccess,
    onUploadBegin,
    descricaoObrigatoria = true,
    produtoId,
    baseURL,
    produtoNome
}) => {
    const [uploadFiles, setUploadFiles] = useState([]);
    const [etapa, setEtapa] = useState('selecao'); // 'selecao' ou 'preenchimento'
    const [imagensComDados, setImagensComDados] = useState([]);
    const [imagemAtualIndex, setImagemAtualIndex] = useState(0);
    const [uploadError, setUploadError] = useState("");
    const [isSending, ] = useState(false);
    const [, setUploadProgress] = useState({});
    const [, setUploadingImageIndex] = useState(-1);
    const [imagensEnviadas, setImagensEnviadas] = useState(new Set());

    // controla quais índices estão enviando em paralelo
    const [uploadingSet, setUploadingSet] = useState(new Set());

    const CONCURRENCY_LIMIT = 3;
    const pendingQueueRef = useRef([]); // fila de tarefas aguardando vaga


    const handleFileSelect = (event) => {
        const files = Array.from(event.target.files);
        setUploadError("");

        const arquivosValidos = [];

        const arquivosNaoImagem = [];
        const arquivosGrandes = [];

        for (const file of files) {
            if (!file.type.startsWith('image/')) {
                arquivosNaoImagem.push(file.name);
                continue;
            }

            const totalImagens = uploadFiles.length + files.length;
            if (totalImagens > 20) {
                setUploadError(`Limite máximo de 20 imagens. Você já tem ${uploadFiles.length} selecionada(s) e está tentando adicionar ${files.length}.`);
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                arquivosGrandes.push(file.name);
                continue;
            }

            arquivosValidos.push(file);
        }

        // Montar mensagens de erro específicas
        const erros = [];
        if (arquivosNaoImagem.length > 0) {
            erros.push(`Arquivos não são imagens: ${arquivosNaoImagem.join(', ')}`);
        }
        if (arquivosGrandes.length > 0) {
            erros.push(`Arquivos ultrapassam 5MB: ${arquivosGrandes.join(', ')}`);
        }
        if (erros.length > 0) {
            setUploadError(erros.join(' | '));
        }

        setUploadFiles([...uploadFiles, ...arquivosValidos]);
    };

    const handleRemoveFile = (index) => {
        const novosArquivos = uploadFiles.filter((_, i) => i !== index);
        setUploadFiles(novosArquivos);
    };

    const handleAvancarParaPreenchimento = () => {
        setImagensComDados(uploadFiles.map(file => ({
            file,
            nome: '',
            descricao: ''
        })));
        setEtapa('preenchimento');
        setUploadError("");
        setImagemAtualIndex(0);
    };

    const handleVoltarParaSelecao = () => {
        setEtapa('selecao');
        setUploadError("");
        setImagensEnviadas(new Set());
        setImagemAtualIndex(0);
    };

    const handleUpdateImagemDados = (index, campo, valor) => {
        const novosdados = [...imagensComDados];
        novosdados[index][campo] = valor;
        setImagensComDados(novosdados);
    };

    const handleSalvarImagemAtual = () => {
        return new Promise((resolve, reject) => {
            const idx = imagemAtualIndex;
            const imgAtual = imagensComDados[idx];

            // Validações
            const valida = descricaoObrigatoria
                ? imgAtual.nome.trim() && imgAtual.descricao.trim()
                : imgAtual.nome.trim();
            if (!valida) {
                setUploadError(
                    descricaoObrigatoria
                        ? "Preencha o nome e a descrição da imagem"
                        : "Preencha o nome da imagem"
                );
                reject(false);
                return;
            }
            if (imgAtual.nome.length > 128) {
                setUploadError("O nome não pode ter mais de 128 caracteres");
                reject(false);
                return;
            }
            if (imgAtual.descricao?.length > 256) {
                setUploadError("A descrição não pode ter mais de 256 caracteres");
                reject(false);
                return;
            }
            // evita duplicar o mesmo índice
            if (imagensEnviadas.has(idx) || uploadingSet.has(idx)) {
                setUploadError("");
                resolve(true);
                return;
            }

            try {
                setUploadError("");
                setUploadProgress((prev) => ({ ...prev, [idx]: 0 }));

                const token = localStorage.getItem("jwtToken");
                if (!token) {
                    reject(false);
                    throw new Error("Token JWT não encontrado.");
                }

                // tenta obter idCliente do JWT
                let idClienteFromJwt = null;
                try {
                    const decoded = jwtDecode(token);
                    idClienteFromJwt = decoded?.id_cliente ?? decoded?.idClienteAipim ?? null;
                } catch (_) { }
                const idClienteAipim = idClienteFromJwt;
                if (!idClienteAipim) {
                    reject(false);
                    throw new Error("idClienteAipim não encontrado.");
                }
                if (!produtoId) {
                    reject(false);
                    throw new Error("idEmpreendimento (produtoId) não informado.");
                }
                if (!baseURL) {
                    reject(false);
                    throw new Error("baseURL não informado.");
                }

                // prepara form-data
                const form = new FormData();
                form.append("idClienteAipim", idClienteAipim);
                form.append("idEmpreendimento", String(produtoId));
                form.append("file", imgAtual.file);
                form.append("nome", imgAtual.nome);
                form.append("descricao", imgAtual.descricao ?? "");

                // id temporário para placeholder + idempotência
                const tempId = `temp-${Date.now()}-${idx}`;
                onUploadBegin?.({
                    tempId,
                    nome: imgAtual.nome,
                    descricao: imgAtual.descricao,
                    file: uploadFiles[idx],
                });

                // tarefa de upload (executada agora ou enfileirada)
                const startUploadTask = () => {
                    // marca este índice como "enviando"
                    setUploadingSet((prev) => new Set(prev).add(idx));

                    axios
                        .post(`${baseURL}/produto/sdr/imagens`, form, {
                            headers: {
                                Authorization: `Bearer ${token}`,
                                "Content-Type": "multipart/form-data",
                                // header opcional para idempotência no backend
                                "X-Upload-Id": tempId,
                            },
                            onUploadProgress: (pe) => {
                                if (!pe.total) return;
                                const pct = Math.round((pe.loaded * 100) / pe.total);
                                setUploadProgress((prev) => ({ ...prev, [idx]: pct }));
                            },
                        })
                        .then(({ data }) => {
                            // marca como enviada
                            setImagensEnviadas((prev) => new Set([...prev, idx]));
                            // troca placeholder pela imagem real
                            onUploadSuccess?.({ ...data, tempId });

                            // === CADASTRO DE JUSTIFICATIVA (CADASTROU) ===
                            try {
                                const decoded = jwtDecode(token);
                                const idUsuario =
                                    decoded?.id_usuario ?? decoded?.idUsuario ?? null;
                                const nomeDoUsuario =
                                    decoded?.nome ?? decoded?.nomeUsuario ?? null;

                                if (!idUsuario || !nomeDoUsuario) {
                                    throw new Error("Dados do usuário não encontrados no token.");
                                }

                                // usa o nome retornado pelo backend, se existir
                                const nomeImagemParaLog = data?.nome ?? imgAtual.nome;

                                const payloadJustificativa = {
                                    idClienteAipim: idClienteAipim,
                                    idUsuario: idUsuario,
                                    idProduto: 2, // mantém o mesmo padrão usado nas outras justificativas
                                    campo: "Galeria de Imagens",
                                    valorNovo: "",
                                    valorAnterior: "",
                                    justificativa: `${nomeDoUsuario} | [CADASTROU] | Produto: ${produtoNome} | Imagem: ${nomeImagemParaLog}`,
                                };

                                console.log("=== PAYLOAD DA JUSTIFICATIVA (CADASTRO) ===");
                                console.log("Payload completo:", payloadJustificativa);

                                axios
                                    .post(
                                        `${baseURL}/produto/Justificativa`,
                                        payloadJustificativa,
                                        {
                                            headers: {
                                                "Content-Type": "application/json",
                                                Authorization: `Bearer ${token}`,
                                            },
                                        }
                                    )
                                    .then(() => {
                                        console.log("Justificativa de cadastro enviada com sucesso");
                                    })
                                    .catch((errJust) => {
                                        console.error(
                                            "Erro ao enviar justificativa de cadastro:",
                                            errJust
                                        );
                                    });
                            } catch (err) {
                                console.error(
                                    "Falha ao montar/enviar justificativa de cadastro:",
                                    err
                                );
                            }

                            resolve(true); // RESOLVE A PROMISE AQUI
                        })
                        .catch((err) => {
                            console.error(`[UPLOAD IMG] erro na imagem ${idx + 1}`, err);
                            setUploadError(
                                err?.response?.data?.message ||
                                err?.message ||
                                "Falha ao enviar a imagem."
                            );
                            reject(false); // REJEITA A PROMISE AQUI
                            // opcional: sinalizar para remover placeholder do tempId
                        })
                        .finally(() => {
                            setUploadingImageIndex(-1);
                            setUploadingSet((prev) => {
                                const s = new Set(prev);
                                s.delete(idx);
                                return s;
                            });
                            // libera próxima tarefa da fila
                            pumpQueue();
                        });
                };

                // respeita o limite de concorrência
                if (uploadingSet.size < CONCURRENCY_LIMIT) {
                    startUploadTask();
                } else {
                    pendingQueueRef.current.push(startUploadTask);
                }

            } catch (err) {
                console.error(`[UPLOAD IMG] erro na imagem ${imagemAtualIndex + 1}`, err);
                setUploadError(err?.message || "Falha ao enviar a imagem.");
                reject(false);
            }
        });
    };

    const pumpQueue = () => {
        if (uploadingSet.size >= CONCURRENCY_LIMIT) return;
        const task = pendingQueueRef.current.shift();
        if (!task) return;
        task(); // dispara o próximo upload
    };


    const handleClose = () => {
        setUploadFiles([]);
        setImagensComDados([]);
        setEtapa('selecao');
        setUploadError("");
        setImagemAtualIndex(0);
        setImagensEnviadas(new Set());
        setUploadProgress({});
        setUploadingImageIndex(-1);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {etapa === 'preenchimento' && (
                        <ArrowBackIcon
                            sx={{ color: '#1976d2', cursor: 'pointer' }}
                            onClick={handleVoltarParaSelecao}
                        />
                    )}
                    <CloudUploadIcon sx={{ color: '#1976d2' }} />
                    <Typography variant="h6">
                        {etapa === 'selecao' ? 'Upload de Imagens' : 'Preencher Dados das Imagens'}
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {etapa === 'selecao' ? (
                        // ETAPA 1: SELEÇÃO DE ARQUIVOS
                        <>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<CloudUploadIcon />}
                                fullWidth
                            >
                                Selecionar Imagens
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    multiple
                                    onChange={handleFileSelect}
                                />
                            </Button>

                            {uploadError && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {uploadError}
                                </Alert>
                            )}

                            {uploadFiles.length > 0 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Typography variant="subtitle2" sx={{ color: '#666' }}>
                                        {uploadFiles.length} {uploadFiles.length === 1 ? 'imagem selecionada' : 'imagens selecionadas'}
                                    </Typography>

                                    {uploadFiles.map((file, index) => (
                                        <Chip
                                            key={index}
                                            label={`${file.name} (${(file.size / 1024).toFixed(2)} KB)`}
                                            onDelete={() => handleRemoveFile(index)}
                                            deleteIcon={<DeleteIcon />}
                                            color="primary"
                                            variant="outlined"
                                        />
                                    ))}
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                                <Button onClick={handleClose}>
                                    Cancelar
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleAvancarParaPreenchimento}
                                    disabled={uploadFiles.length === 0}
                                >
                                    Próximo: Preencher Dados
                                </Button>
                            </Box>
                        </>
                    ) : (
                        // ETAPA 2: PREENCHIMENTO DE DADOS
                        <>
                            {uploadError && (
                                <Typography color="error" variant="body2">
                                    Erro ao fazer upload do arquivo.
                                </Typography>
                            )}

                            <Box sx={{
                                maxHeight: '60vh',
                                overflowY: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2
                            }}>
                                {imagensComDados.length > 0 && (
                                    <>
                                        <Typography variant="body2" sx={{ mb: 2, color: '#666', textAlign: 'center' }}>
                                            Imagem {imagemAtualIndex + 1} de {imagensComDados.length}
                                        </Typography>

                                        <Box
                                            sx={{
                                                border: '1px solid #e0e0e0',
                                                p: 2,
                                                borderRadius: 1,
                                                bgcolor: '#fafafa'
                                            }}
                                        >
                                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                                {imagensComDados[imagemAtualIndex].file.name}
                                            </Typography>

                                            <Box
                                                component="img"
                                                src={URL.createObjectURL(imagensComDados[imagemAtualIndex].file)}
                                                sx={{
                                                    width: '100%',
                                                    maxWidth: 200,
                                                    height: 'auto',
                                                    borderRadius: 1,
                                                    mb: 2
                                                }}
                                                alt={imagensComDados[imagemAtualIndex].file.name}
                                            />


                                            <TextField
                                                label="Nome da Imagem"
                                                disabled={isSending || imagensEnviadas.has(imagemAtualIndex)}
                                                value={imagensComDados[imagemAtualIndex].nome}
                                                onChange={(e) => handleUpdateImagemDados(imagemAtualIndex, 'nome', e.target.value)}
                                                fullWidth
                                                required
                                                error={imagensComDados[imagemAtualIndex].nome.length > 128}
                                                helperText={`${imagensComDados[imagemAtualIndex].nome.length}/128 caracteres`}
                                                sx={{ mb: 2 }}
                                            />

                                            <TextField
                                                label="Descrição"
                                                disabled={isSending || imagensEnviadas.has(imagemAtualIndex)}
                                                value={imagensComDados[imagemAtualIndex].descricao}
                                                onChange={(e) => handleUpdateImagemDados(imagemAtualIndex, 'descricao', e.target.value)}
                                                fullWidth
                                                multiline
                                                rows={2}
                                                required={descricaoObrigatoria}
                                                error={imagensComDados[imagemAtualIndex].descricao.length > 256}
                                                helperText={`${imagensComDados[imagemAtualIndex].descricao.length}/256 caracteres`}
                                            />
                                        </Box>
                                    </>
                                )}
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', mt: 2 }}>
                                <Button onClick={handleVoltarParaSelecao}>
                                    Voltar
                                </Button>

                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    {imagemAtualIndex < imagensComDados.length - 1 ? (
                                        <Button
                                            variant="contained"
                                            onClick={() => {
                                                // Dispara o upload mas NÃO aguarda
                                                handleSalvarImagemAtual().catch(() => {
                                                    console.log('Erro ao salvar imagem');
                                                });

                                                // Avança IMEDIATAMENTE para a próxima
                                                setImagemAtualIndex(imagemAtualIndex + 1);
                                            }}
                                            disabled={
                                                (descricaoObrigatoria
                                                    ? !imagensComDados[imagemAtualIndex].nome.trim() || !imagensComDados[imagemAtualIndex].descricao.trim()
                                                    : !imagensComDados[imagemAtualIndex].nome.trim()
                                                )
                                            }
                                        >
                                            Salvar e Próxima
                                        </Button>
                                    ) : (
                                        // ÚLTIMO BOTÃO - mantém como estava com await
                                        <Button
                                            variant="contained"
                                            onClick={() => {
                                                // Dispara o upload mas NÃO aguarda
                                                handleSalvarImagemAtual().catch(() => {
                                                    console.log('Erro ao salvar última imagem');
                                                });

                                                // Fecha IMEDIATAMENTE
                                                handleClose();
                                            }}
                                            disabled={
                                                descricaoObrigatoria
                                                    ? !imagensComDados[imagemAtualIndex].nome.trim() ||
                                                    !imagensComDados[imagemAtualIndex].descricao.trim()
                                                    : !imagensComDados[imagemAtualIndex].nome.trim()
                                            }
                                            sx={{ minWidth: 200 }}
                                        >
                                            {`Adicionar ${imagensComDados.length} ${imagensComDados.length === 1 ? "Imagem" : "Imagens"}`}
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default UploadModal;